import React, { useEffect, useMemo } from 'react';
import { LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom';
import { getUid } from 'ol/util';
import Feature from 'ol/Feature';
import OLDrawInteraction, { createBox } from 'ol/interaction/Draw';
import OLModifyInteraction from 'ol/interaction/Modify';
import { INTERACTION_KIND, GEOMETRY_TYPE } from '@tmapy/config';
import { getSimpleGeometryType } from '../../utils/geometry/getSimpleGeometryType';
import { replaceUndefinedCoord } from '../../utils/geometry/replaceUndefinedCoord';
import { styleFactory } from '../../utils/styleFactory';
import { useLayer } from '../../hooks/useLayer';
import { actionInteractionDrawFeatures, actionInteractionSelectFeatures, } from '../../actions';
import { useOLMap } from '../MapContext';
export const InteractionDraw = React.memo(({ dispatch, type, geometryLayout, modify, layerId, style }) => {
    const map = useOLMap();
    const layer = useLayer(layerId);
    const source = layer?.getSource();
    const geometryType = getSimpleGeometryType(type);
    const drawInteraction = useMemo(() => {
        const styleInstance = styleFactory(style);
        const interaction = new OLDrawInteraction({
            type: geometryType === GEOMETRY_TYPE.BOX ? GEOMETRY_TYPE.CIRCLE : geometryType,
            style: (f, resolution) => {
                if (f instanceof Feature) {
                    // TODO - podpora prave kresleneho prvku OL, kdy jeste fyzicky neexistuje v reduxu ani v ol.layer
                    // console.log('F', f.getGeometry()?.getType());
                }
                return typeof styleInstance === 'function' ? styleInstance(f, resolution) : styleInstance;
            },
            source,
            geometryFunction: geometryType === GEOMETRY_TYPE.BOX
                ? createBox()
                : (coordinates, geometry) => {
                    if (geometry) {
                        if (geometryType === GEOMETRY_TYPE.POLYGON) {
                            if (Array.isArray(coordinates[0]) && coordinates[0].length) {
                                // Add a closing coordinate to match the first
                                geometry.setCoordinates([
                                    coordinates[0]?.concat([
                                        coordinates[0]?.[0],
                                    ]),
                                ], geometryLayout);
                            }
                            else {
                                geometry.setCoordinates([], geometryLayout);
                            }
                        }
                        else {
                            geometry.setCoordinates(coordinates, geometryLayout);
                        }
                        return geometry;
                    }
                    else {
                        switch (geometryType) {
                            case GEOMETRY_TYPE.POINT:
                                for (let l = 0; l < geometryLayout.length; l++) {
                                    coordinates[l] = coordinates[l] ?? 0;
                                }
                                return new Point(coordinates, geometryLayout);
                            case GEOMETRY_TYPE.LINE_STRING:
                                return new LineString(coordinates, geometryLayout);
                            case GEOMETRY_TYPE.POLYGON:
                                return new Polygon(coordinates, geometryLayout);
                            default:
                                console.error(`'${geometryType}' is not a supported geometryType`);
                                return geometry;
                        }
                    }
                },
        });
        if (process.env.NODE_ENV === 'development') {
            console.log('new OLDrawInteraction', interaction);
        }
        return interaction;
    }, [source, style, geometryType, geometryLayout]);
    const modifyInteraction = useMemo(() => {
        if (!modify || !source)
            return undefined;
        const interaction = new OLModifyInteraction({ source });
        if (process.env.NODE_ENV === 'development') {
            console.log('new OLModifyInteraction', interaction);
        }
        return interaction;
    }, [modify, source]);
    // keep synced with map
    useEffect(() => {
        if (!dispatch)
            return;
        const interactions = map.getInteractions();
        interactions.push(drawInteraction);
        modifyInteraction && interactions.push(modifyInteraction);
        //update features
        dispatch(actionInteractionDrawFeatures(layerId, [], INTERACTION_KIND.NONE));
        return () => {
            interactions.remove(drawInteraction);
            modifyInteraction && interactions.remove(modifyInteraction);
        };
    }, [drawInteraction, map, modifyInteraction, dispatch, source, layerId, geometryType]);
    // dispatch
    useEffect(() => {
        if (!dispatch)
            return;
        const onDrawEnd = (e) => {
            const feature = e.feature;
            feature.set('layerId', layerId);
            feature.set('id', getUid(feature));
            if (type === GEOMETRY_TYPE.TEXT) {
                feature.set('type', GEOMETRY_TYPE.TEXT);
            }
            let geometry = feature.getGeometry();
            const coordinates = replaceUndefinedCoord(geometry.getCoordinates());
            switch (type) {
                case GEOMETRY_TYPE.MULTI_POINT:
                    geometry = new MultiPoint([coordinates]);
                    break;
                case GEOMETRY_TYPE.MULTI_LINE_STRING:
                    geometry = new MultiLineString([coordinates]);
                    break;
                case GEOMETRY_TYPE.MULTI_POLYGON:
                    geometry = new MultiPolygon([coordinates]);
                    break;
                default:
                    geometry.setCoordinates(coordinates);
            }
            feature.setGeometry(geometry);
            // update features
            feature &&
                dispatch(actionInteractionDrawFeatures(layerId, [feature], INTERACTION_KIND.DRAW_END));
        };
        const onModifyEnd = (e) => {
            dispatch(actionInteractionDrawFeatures(layerId, e.features.getArray(), INTERACTION_KIND.MODIFY_END));
        };
        const onDrawStart = () => {
            dispatch(actionInteractionDrawFeatures(layerId, [], INTERACTION_KIND.DRAW_START));
            dispatch(actionInteractionSelectFeatures(layerId, []));
        };
        drawInteraction.on('drawstart', onDrawStart);
        drawInteraction.on('drawend', onDrawEnd);
        modifyInteraction?.on('modifyend', onModifyEnd);
        return () => {
            drawInteraction.un('drawstart', onDrawStart);
            drawInteraction.un('drawend', onDrawEnd);
            modifyInteraction?.un('modifyend', onModifyEnd);
        };
    }, [dispatch, drawInteraction, source, modifyInteraction, layerId, type]);
    return null;
});
//# sourceMappingURL=InteractionDraw.js.map